import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Eye, ExternalLink, PauseCircle, PlayCircle, Trash2, RotateCcw, X, ShieldCheck, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/marketing/BrandMark";
import { api, cardCls } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Never");

const Badge = ({ children, tone = "grey" }) => {
  const tones = {
    grey: "bg-guinep-soft text-guinep-mid",
    green: "bg-guinep-deep text-white",
    red: "bg-red-100 text-red-700",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};

const ConfirmModal = ({ action, user, onCancel, onConfirm, busy }) => {
  const labels = {
    suspend: { title: "Suspend account?", body: `${user.name || user.email} won't be able to sign in, and their public page will be hidden until reactivated.` },
    reactivate: { title: "Reactivate account?", body: `${user.name || user.email} will be able to sign in again, and their public page will be restored if it was live.` },
    reset: { title: "Reset onboarding?", body: `This deletes ${user.business_name || "their"} page content (business info, services, Now updates) so they can set up again. Their account stays.` },
    delete: { title: "Delete account?", body: `This permanently deletes ${user.email}, their business page, services, updates and enquiries. This cannot be undone.` },
  };
  const l = labels[action];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-5" data-testid="admin-confirm-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <h3 className="font-serif text-xl font-medium text-guinep-ink">{l.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-guinep-mute">{l.body}</p>
        <div className="mt-6 flex gap-3">
          <button onClick={onCancel} data-testid="confirm-cancel" className="flex-1 rounded-xl border border-guinep-line py-3 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-action"
            className={`flex-1 rounded-xl py-3 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${action === "delete" || action === "suspend" ? "bg-red-600 hover:bg-red-700" : "bg-guinep-deep hover:bg-guinep-mid"}`}
          >
            {busy ? "Working…" : l.title.replace("?", "")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (q = "") => {
    const data = await api(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setUsers(data.users);
  };

  useEffect(() => {
    load()
      .then(() => setState("ok"))
      .catch((e) => setState(e.message.includes("403") || e.message.includes("Admins") ? "forbidden" : "guest"));
  }, []);

  useEffect(() => {
    if (state !== "ok") return;
    const t = setTimeout(() => load(search).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [search, state]);

  const runAction = async () => {
    const { action, user } = confirm;
    setBusy(true);
    try {
      if (action === "delete") {
        await api(`/admin/users/${user.user_id}`, { method: "DELETE" });
        toast.success("Account deleted");
      } else if (action === "reset") {
        await api(`/admin/users/${user.user_id}/reset-onboarding`, { method: "POST" });
        toast.success("Onboarding reset");
      } else {
        await api(`/admin/users/${user.user_id}/${action}`, { method: "POST" });
        toast.success(action === "suspend" ? "Account suspended" : "Account reactivated");
      }
      setConfirm(null);
      setViewing(null);
      load(search);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-guinep-line border-t-guinep-deep" />
      </div>
    );
  }

  if (state !== "ok") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F7F7F5] px-5 text-center font-sans" data-testid="admin-forbidden">
        <ShieldCheck size={28} className="text-guinep-mid" />
        <h1 className="mt-4 font-serif text-2xl font-medium text-guinep-ink">
          {state === "forbidden" ? "Admins only" : "Sign in required"}
        </h1>
        <p className="mt-2 max-w-xs text-sm text-guinep-mute">
          {state === "forbidden" ? "This area is restricted to the Guinep Seed platform admin." : "Please sign in with an admin account."}
        </p>
        <Link to={state === "forbidden" ? "/" : "/login"} className="mt-6 rounded-full bg-guinep-deep px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid">
          {state === "forbidden" ? "Back to Guinep Seed" : "Sign in"}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F5] font-sans" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-guinep-line/50 bg-[#F7F7F5]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-guinep-soft"><BrandMark size={14} /></span>
            <span className="font-serif text-base font-semibold tracking-tight text-guinep-ink">Guinep Seed Admin</span>
          </div>
          <Link to="/app" data-testid="admin-back-app" className="text-xs font-medium text-guinep-mute transition-colors hover:text-guinep-ink">Owner dashboard</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-7">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-guinep-mute/60" />
          <input
            data-testid="admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, business or slug…"
            className="w-full rounded-xl border border-guinep-line bg-white py-3.5 pl-11 pr-4 text-sm text-guinep-ink outline-none transition-colors focus:border-guinep-mid placeholder:text-guinep-mute/50"
          />
        </div>
        <p className="mt-3 text-xs text-guinep-mute" data-testid="admin-user-count">{users.length} {users.length === 1 ? "user" : "users"}</p>

        <div className="mt-4 space-y-3">
          {users.map((u) => (
            <div key={u.user_id} className={`${cardCls} p-4`} data-testid="admin-user-card">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-guinep-ink">{u.name || "—"}</p>
                <Badge tone={u.signup_method === "google" ? "green" : "grey"}>{u.signup_method === "google" ? "Google" : "Email"}</Badge>
                {u.is_suspended && <Badge tone="red">Suspended</Badge>}
                <span className="ml-auto text-xs text-guinep-mute">{fmtDate(u.created_at)}</span>
              </div>
              <p className="mt-1 text-[13px] text-guinep-mute">{u.email}</p>

              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] sm:grid-cols-4">
                <div><span className="text-guinep-mute/70">Business</span><p className="font-medium text-guinep-ink">{u.business_name || "—"}</p></div>
                <div><span className="text-guinep-mute/70">Slug</span><p className="font-medium text-guinep-ink">{u.slug ? `/${u.slug}` : "—"}</p></div>
                <div><span className="text-guinep-mute/70">Onboarding</span><p className="font-medium text-guinep-ink">{u.onboarding_complete ? "Yes" : "No"}</p></div>
                <div><span className="text-guinep-mute/70">Page</span><p className="font-medium text-guinep-ink">{u.is_live ? "Live" : "Draft"}</p></div>
                <div><span className="text-guinep-mute/70">Subscription</span><p className="font-medium capitalize text-guinep-ink" data-testid="admin-subscription-status">{({ trial: "Trial", active: "Active", past_due: "Past Due", cancelled: "Cancelled", expired: "Expired", suspended: "Suspended" })[u.subscription_status] || u.subscription_status}</p></div>
                <div><span className="text-guinep-mute/70">Services</span><p className="font-medium text-guinep-ink">{u.services_count}</p></div>
                <div><span className="text-guinep-mute/70">Enquiries</span><p className="font-medium text-guinep-ink">{u.enquiries_count}</p></div>
                <div className="col-span-2"><span className="text-guinep-mute/70">Last activity</span><p className="font-medium text-guinep-ink">{fmtDateTime(u.last_activity)}</p></div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 border-t border-guinep-line/60 pt-3">
                <button data-testid={`admin-view-${u.user_id}`} onClick={() => setViewing(u)} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
                  <Eye size={13} /> View account
                </button>
                {u.business_id && (
                  <button data-testid={`admin-view-dashboard-${u.user_id}`} onClick={() => navigate(`/app?admin_view=${u.business_id}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-deep/40 bg-guinep-soft/60 px-3 py-2 text-xs font-semibold text-guinep-deep transition-colors hover:bg-guinep-soft">
                    <LayoutDashboard size={13} /> View dashboard
                  </button>
                )}
                {u.slug && u.is_live ? (
                  <a data-testid={`admin-page-${u.user_id}`} href={`/${u.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
                    <ExternalLink size={13} /> View public page
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line/50 px-3 py-2 text-xs font-medium text-guinep-mute/50">No live page</span>
                )}
                {u.is_self ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line/50 px-3 py-2 text-xs font-medium text-guinep-mute/50">This is you</span>
                ) : u.is_suspended ? (
                  <button data-testid={`admin-reactivate-${u.user_id}`} onClick={() => setConfirm({ action: "reactivate", user: u })} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3 py-2 text-xs font-semibold text-guinep-deep transition-colors hover:bg-guinep-soft">
                    <PlayCircle size={13} /> Reactivate
                  </button>
                ) : (
                  <button data-testid={`admin-suspend-${u.user_id}`} onClick={() => setConfirm({ action: "suspend", user: u })} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3 py-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50">
                    <PauseCircle size={13} /> Suspend
                  </button>
                )}
                {u.business_name && !u.is_self && (
                  <button data-testid={`admin-reset-${u.user_id}`} onClick={() => setConfirm({ action: "reset", user: u })} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
                    <RotateCcw size={13} /> Reset onboarding
                  </button>
                )}
                {!u.is_self && (
                  <button data-testid={`admin-delete-${u.user_id}`} onClick={() => setConfirm({ action: "delete", user: u })} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && <p className="py-10 text-center text-sm text-guinep-mute">No users match your search.</p>}
        </div>
      </main>

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-5" data-testid="admin-view-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-medium text-guinep-ink">{viewing.name || "Account"}</h3>
              <button onClick={() => setViewing(null)} className="text-guinep-mute hover:text-guinep-ink"><X size={18} /></button>
            </div>
            <dl className="mt-4 space-y-2.5 text-sm">
              {[["Email", viewing.email], ["Sign-up method", viewing.signup_method === "google" ? "Google" : "Email / password"],
                ["Business", viewing.business_name || "—"], ["Slug", viewing.slug ? `/${viewing.slug}` : "—"],
                ["Page status", viewing.is_live ? "Live" : "Draft"], ["Onboarding", viewing.onboarding_complete ? "Complete" : "Not complete"],
                ["Joined", fmtDate(viewing.created_at)], ["Last activity", fmtDateTime(viewing.last_activity)],
                ["Services", viewing.services_count], ["Enquiries", viewing.enquiries_count],
                ["Subscription", viewing.subscription_status], ["Trial ends", fmtDate(viewing.trial_end)],
                ["Next billing", fmtDate(viewing.next_billing_date)],
                ["Status", viewing.is_suspended ? "Suspended" : "Active"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-guinep-line/50 pb-2">
                  <dt className="text-guinep-mute">{k}</dt>
                  <dd className="text-right font-medium text-guinep-ink">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {confirm && <ConfirmModal action={confirm.action} user={confirm.user} busy={busy} onCancel={() => setConfirm(null)} onConfirm={runAction} />}
    </div>
  );
};

export default Admin;
